// src/redux/accessibilitySlice.js
import { createSlice } from '@reduxjs/toolkit';

// Configuración inicial más detallada
const initialState = {
  reducedMotion: false,
  highContrast: false,
  fontSize: 'normal', // 'small', 'normal', 'large', 'xlarge'
  keyboardNavigation: true,
  darkMode: false,
  currentLanguage: 'es', // Para soporte multilingüe
  focusVisible: true, // Para resaltar elementos enfocados
  ariaLiveRegions: {}, // Para mensajes dinámicos
  tabIndex: 0 // Para manejo personalizado de tabulación
};

export const accessibilitySlice = createSlice({
  name: 'accessibility',
  initialState,
  reducers: {
    toggleReducedMotion: (state) => {
      state.reducedMotion = !state.reducedMotion;
      // Aplicar inmediatamente al estado global
      document.documentElement.classList.toggle('reduced-motion', state.reducedMotion);
    },
    toggleHighContrast: (state) => {
      state.highContrast = !state.highContrast;
      document.documentElement.classList.toggle('high-contrast', state.highContrast);
    },
    setFontSize: (state, action) => {
      const validSizes = ['small', 'normal', 'large', 'xlarge'];
      state.fontSize = validSizes.includes(action.payload) ? action.payload : 'normal';
      document.documentElement.style.setProperty('--font-size-multiplier', 
        state.fontSize === 'small' ? 0.875 : 
        state.fontSize === 'large' ? 1.25 : 
        state.fontSize === 'xlarge' ? 1.5 : 1
      );
    },
    toggleKeyboardNavigation: (state) => {
      state.keyboardNavigation = !state.keyboardNavigation;
      document.documentElement.classList.toggle('keyboard-navigation', state.keyboardNavigation);
    },
    toggleDarkMode: (state) => {
      state.darkMode = !state.darkMode;
      document.documentElement.setAttribute('data-theme', state.darkMode ? 'dark' : 'light');
    },
    setAriaLive: (state, action) => {
      // Para mensajes de screen readers
      state.ariaLiveRegions = {
        ...state.ariaLiveRegions,
        [action.payload.region]: action.payload.message
      };
      // Limpiar después de 5 segundos
      setTimeout(() => {
        state.ariaLiveRegions[action.payload.region] = '';
      }, 5000);
    },
    setFocusElement: (state, action) => {
      // Para manejar foco programático
      if (action.payload) {
        document.getElementById(action.payload)?.focus();
      }
    }
  }
});

// Exportar acciones y reducer
export const { 
  toggleReducedMotion, 
  toggleHighContrast, 
  setFontSize,
  toggleKeyboardNavigation,
  toggleDarkMode,
  setAriaLive,
  setFocusElement
} = accessibilitySlice.actions;

// Selectores útiles
export const selectAccessibility = (state) => state.accessibility;
export const selectAriaLive = (region) => (state) => 
  state.accessibility.ariaLiveRegions[region] || '';

export default accessibilitySlice.reducer;